<script setup lang="ts">
import type { Schemas } from "#shopware";
import type { CmsElementProductDescriptionReviews } from "@shopware/composables";
import { getTranslatedProperty, getProductName } from "@shopware/helpers";
import { useCmsTranslations } from "@shopware/composables";
import { computed, ref } from "vue";
import { defu } from "defu";
import { useProduct, useUser, useShopwareContext } from "#imports";
import { decodeHTML } from "entities";
import type { NodeObject } from "@shopware/cms-base-layer/helpers/html-to-vue/getOptionsFromNode";
import { getOptionsFromNode } from "@shopware/cms-base-layer/helpers/html-to-vue/getOptionsFromNode";
import { renderHtml } from "@shopware/cms-base-layer/helpers/html-to-vue/renderToHtml";

const props = defineProps<{
  content: CmsElementProductDescriptionReviews;
}>();

type Translations = {
  product: {
    description: string;
    reviews: string;
    messages: {
      reviewAdded: string;
    };
  };
};

let translations: Translations = {
  product: {
    description: "Description",
    reviews: "Reviews",
    messages: {
      reviewAdded: "Thank you for submitting your review",
    },
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const currentTab = ref<number>(1);
const { product } = useProduct(props.content.data?.product);

const description = computed(() =>
  getTranslatedProperty(product.value, "description")
);

const toggleTabs = (tabNumber: number) => {
  currentTab.value = tabNumber;
};

const reviews: Ref<Schemas["ProductReview"][]> = ref([]);

const { apiClient } = useShopwareContext();
const { isLoggedIn } = useUser();
const reviewAdded = ref(false);

const fetchReviews = async () => {
  const reviewsResponse = await apiClient.invoke(
    "readProductReviews post /product/{productId}/reviews",
    {
      pathParams: { productId: product.value.id },
    }
  );
  reviews.value = reviewsResponse.data.elements || [];
};
const handleReviewAdded = () => {
  reviewAdded.value = true;
  fetchReviews();
};

const Zertifikates = computed(() => {
  return product?.value?.properties?.filter(
    (property) =>
      property.group.customFields?.custom_product_zertifikates_enabled ===
        true && !!property.media
  );
});

onMounted(async () => {
  if (props.content.data?.reviews?.elements) {
    reviews.value = props.content.data.reviews.elements;
  }
  if (
    product?.value?.translated?.customFields
      ?.custom_product_fields_shortdescription
  ) {
    currentTab.value = 1;
  } else if (product?.value?.sortedProperties?.length > 0) {
    currentTab.value = 2;
  } else if (product?.value?.translated?.customFields?.custom_application) {
    currentTab.value = 3;
  } else if (
    product?.value?.translated?.customFields?.custom_field_of_application
  ) {
    currentTab.value = 4;
  } else if (pdfMediaUrls.value?.length > 0) {
    currentTab.value = 5;
  }
});

const pdfMediaUrls = ref<string[]>([]);

const fetchMediaDetails = async (mediaIds: string[]): Promise<string[]> => {
  try {
    const response = await apiClient.invoke("readMedia post /media", {
      body: {
        ids: mediaIds,
      },
    });
    // Map the response to get media URLs
    return response.data.map((media: any) => media);
  } catch (error) {
    console.error("Failed to fetch media details:", error);
    return [];
  }
};

// Watch for changes to `product.customFields`
watch(
  () => product.value.translated?.customFields,
  async (customFields) => {
    const mediaIds: string[] = [];

    // Extract media IDs from `custom_pdf_${i}` fields
    for (let i = 0; i <= 10; i++) {
      const mediaId = customFields[`custom_pdf_${i}`];
      if (mediaId) {
        mediaIds.push(mediaId);
      }
    }

    // Fetch media URLs if there are IDs
    if (mediaIds.length > 0) {
      pdfMediaUrls.value = await fetchMediaDetails(mediaIds);
    }
  },
  { immediate: true }
);

const groupedProperties = computed(() => {
  const properties = product?.value?.properties || [];
  const groups: {
    [key: string]: {
      group: Schemas["PropertyGroup"];
      properties: Schemas["PropertyGroupOption"][];
    };
  } = {};
  properties.forEach((property) => {
    const groupId = property.group?.id;
    if (!groups[groupId]) {
      groups[groupId] = {
        group: property.group,
        properties: [],
      };
    }
    groups[groupId].properties.push(property);
  });
  return groups;
});

const context = getCurrentInstance();

const CmsProductDescriptionRender = defineComponent({
  props: {
    text: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const { resolveUrl } = useUrlResolver();

    const config = {
      textTransformer: (text: string) => decodeHTML(text),
      extraComponentsMap: {
        link: {
          conditions(node: NodeObject) {
            return (
              node.type === "tag" &&
              node.name === "a" &&
              !node.attrs?.class?.match(/btn\s?/)
            );
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            const _class = node?.class;
            return createElement(
              "a",
              {
                class: _class,
                ...getOptionsFromNode(node, resolveUrl).attrs,
                href: node.attrs?.href,
              },
              [...children]
            );
          },
        },
        button: {
          conditions(node: NodeObject) {
            return (
              node.type === "tag" &&
              node.name === "a" &&
              node.attrs?.class?.match(/btn\s?/)
            );
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            let _class = "";
            if (node?.class) {
              const btnClass = "";

              _class = node?.class
                ?.replace("btn-secondary", `${btnClass} btn-outline`)
                .replace("btn-primary", `${btnClass} btn`);
            }

            return createElement(
              "a",
              {
                class: _class,
                ...getOptionsFromNode(node, resolveUrl).attrs,
              },
              [...children]
            );
          },
        },
        font: {
          conditions(node: NodeObject) {
            return node.type === "tag" && node.name === "font";
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            let newStyle = null;
            const styleColor = node?.attrs?.color;
            if (styleColor) {
              const currentStyle = node.attrs?.style ?? "";
              newStyle = `color:${styleColor};${currentStyle}`;
              delete node.attrs?.color;
            }

            return createElement(
              "span",
              {
                style: newStyle,
                ...getOptionsFromNode(node, resolveUrl).attrs,
              },
              [...children]
            );
          },
        },
        img: {
          conditions(node: NodeObject) {
            return node.type === "tag" && node.name === "img";
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            return createElement(
              "img",
              getOptionsFromNode(node, resolveUrl)?.attrs
            );
          },
        },
      },
    };

    const rawHtml = computed(() => {
      if (props.text) {
        return props.text;
      } else {
        return "<div class='cms-element-text missing-content-element'></div>";
      }
    });

    return () =>
      h("div", {}, renderHtml(rawHtml.value, config, h, context, resolveUrl));
  },
});
</script>

<template>
  <section class="py-10 lg:pt-75 !pb-0 prose-p:text-sm">
    <div v-if="product" class="container">
      <div class="laptop:flex flex-wrap -mx-[0.94rem] gap-y-14">
        <div class="laptop:w-[70%] px-[0.94rem]">
          <h4 class="font-bold font-base mb-10">
            {{ $t("product.detail.title") }}
          </h4>
          <div class="relative text-sm">
            <div
              class="details_nav prose-a:rounded-t px-0 py-2 prose-a:px-2.5 prose-a:py-3 uppercase font-bold laptop:[&>.slick-list>.slick-track]:!translate-x-0 border-b border-skin-primary overflow-x-auto whitespace-nowrap no-scrollbar"
            >
              <a
                v-if="
                  product?.translated?.customFields
                    ?.custom_product_fields_shortdescription
                "
                class="text-skin-secondary transition-all duration-300"
                :class="{ 'bg-skin-theme text-skin-white': currentTab === 1 }"
                href="javascript:void(0);"
                @click="() => toggleTabs(1)"
                >{{ $t("product.detail.description") }}</a
              >
              <a
                v-if="Object.keys(groupedProperties).length > 0"
                class="text-skin-secondary transition-all duration-300"
                :class="{ 'bg-skin-theme text-skin-white': currentTab === 2 }"
                href="javascript:void(0);"
                @click="() => toggleTabs(2)"
                >{{ $t("product.detail.details") }}</a
              >
              <a
                v-if="product?.translated?.customFields?.custom_application"
                class="text-skin-secondary transition-all duration-300"
                :class="{ 'bg-skin-theme text-skin-white': currentTab === 3 }"
                href="javascript:void(0);"
                @click="() => toggleTabs(3)"
                >{{ $t("product.detail.applications") }}</a
              >
              <a
                v-if="
                  product?.translated?.customFields?.custom_field_of_application
                "
                class="text-skin-secondary transition-all duration-300"
                :class="{ 'bg-skin-theme text-skin-white': currentTab === 4 }"
                href="javascript:void(0);"
                @click="() => toggleTabs(4)"
                >{{ $t("product.detail.areaOfApplication") }}</a
              >
              <a
                v-if="pdfMediaUrls.length > 0"
                class="text-skin-secondary transition-all duration-300"
                :class="{ 'bg-skin-theme text-skin-white': currentTab === 5 }"
                href="javascript:void(0);"
                @click="() => toggleTabs(5)"
                >{{ $t("product.detail.safetyDataSheets") }}</a
              >
              <!-- <a
                class="text-skin-secondary transition-all duration-300"
                :class="{ 'bg-skin-theme text-skin-white': currentTab === 6 }"
                href="javascript:void(0);"
                @click="() => toggleTabs(6)"
                >{{ $t("product.reviews") }} ({{ reviews.length }})</a
              > -->
            </div>
            <div class="details_view">
              <div
                class="py-10"
                :class="[currentTab !== 1 ? 'hidden' : 'block']"
              >
                <div class="product-description">
                  <CmsProductDescriptionRender
                    :text="
                      product?.translated?.customFields
                        ?.custom_product_fields_shortdescription
                    "
                  />
                </div>
              </div>
              <div
                class="py-10"
                :class="[currentTab !== 2 ? 'hidden' : 'block']"
              >
                <div class="w-full overflow-x-auto">
                  <table class="w-full">
                    <tbody>
                      <tr
                        v-for="(property, key, index) in groupedProperties"
                        class="flex items-center justify-normal pb-5 mb-5"
                        :class="{
                          'border-b border-skin-primary':
                            index !== Object.keys(groupedProperties).length - 1,
                        }"
                      >
                        <th
                          class="flex-none font-semibold lg:min-w-[35%] lg:w-[35%] mr-5 p-0 text-left w-[50%]"
                        >
                          <span class="align-middle break-words">{{
                            property?.group?.translated?.name
                          }}</span>
                        </th>
                        <td class="text-left">
                          <p class="break-words">
                            <template
                              v-for="(option, index) in property?.properties"
                              >{{ option.translated.name
                              }}<span
                                v-if="
                                  index !== property?.properties?.length - 1
                                "
                                >,
                              </span>
                            </template>
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <!-- <div
                  v-html="
                    product?.customFields
                      ?.custom_product_fields_listing_description
                  "
                ></div> -->
              </div>
              <div
                class="py-10"
                :class="[currentTab !== 3 ? 'hidden' : 'block']"
              >
                <div
                  class="product-description description-table"
                  v-html="product?.translated?.customFields?.custom_application"
                ></div>
              </div>
              <div
                class="py-10"
                :class="[currentTab !== 4 ? 'hidden' : 'block']"
              >
                <div class="product-description">
                  <CmsProductDescriptionRender
                    :text="
                      product?.translated?.customFields
                        ?.custom_field_of_application
                    "
                  />
                </div>
              </div>
              <div
                class="py-10"
                :class="[currentTab !== 5 ? 'hidden' : 'block']"
              >
                <div
                  v-for="(media, index) in pdfMediaUrls"
                  :key="index"
                  class="mt-2"
                >
                  <NuxtLink
                    class="link hover:text-skin-theme"
                    :href="media?.url"
                    target="_blank"
                  >
                    <i class="fa fa-file-pdf text text-skin-theme mr-2"></i>
                    {{ media?.title || media?.fileName }}</NuxtLink
                  >
                </div>
              </div>
              <div
                class="py-10"
                :class="[currentTab !== 6 ? 'hidden' : 'block']"
              >
                <ProductReviews :product="product" :reviews="reviews" />
                <ProductReviewsForm
                  class="border border-skin-primary my-2 p-2 rounded-lg"
                  v-if="isLoggedIn && !reviewAdded"
                  :product-id="product.id"
                  @success="handleReviewAdded"
                />
                <div v-if="reviewAdded">
                  {{ translations.product.messages.reviewAdded }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="Zertifikates?.length" class="laptop:w-[30%] px-[0.94rem]">
          <h4 class="font-bold font-base mb-10">
            {{ $t("product.detail.certificates") }}
          </h4>
          <div class="grid grid-cols-3 gap-x-3.5 gap-y-10 prose-img:mb-3.5">
            <template v-for="zertifikate in Zertifikates">
              <div v-if="zertifikate.media">
                <NuxtImg
                  v-if="zertifikate.media"
                  loading="lazy"
                  format="webp"
                  :src="zertifikate.media?.url"
                  :alt="zertifikate.name"
                  height="64"
                  width="64"
                  class="object-contain"
                />
                <p class="break-words">{{ zertifikate.name }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none; /* Webkit */
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
